import React, { Dispatch, SetStateAction, FC, useState } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { Spin, Form } from 'antd';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { updateAprAndAprRelatedClosingCost } from '../../../../../utils/updateAprAndAprRelatedClosingCost';
import { EnumReportTypes, ILoanValue } from '../../../../../types/reports';
import { prepareLoanDataFromForm } from '../../../../../utils/prepareData';
import { useUpdateLoansClosingCostAmount } from '../../../../../hooks/loans';
import { useContextReports } from '../../../../../context/reports';
import { ILoan } from '../closingCostsDetails.types';
import { formatAmount } from '../../../../../utils/formatAmount';
import { useContextNotifications } from '../../../../../context/notifications';
import { useReportLoanUpdate } from '../../../../../hooks/reports';
import { EnumClosingCostType } from '../../../../../types/settings';
import { ILoanClosingCost } from '../../../../../hooks/users';
import './LoanClosingCost.scss'
import { calculateLoanAmountWithUip } from '../../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../../utils/calculateDownPaymentPrice';
import { calculatePrincipalInterest } from '../../../../../utils/calculatePrincipalInterest';

type Props = {
  value: any;
  record: any;
  refetchData: () => Promise<void>;
  calculateColumnTotals: (loanId: number) => number;
  setReportLoanClosingCosts: Dispatch<SetStateAction<ILoanClosingCost[]>>;
  loan: ILoan;
  nonMutableColumnsMapping: { loanPoints: string; uip: string; vaFundingFee: string };
  currentClosingCosts: any[];
  isFirstItemInRefinance: boolean;
  loanIndex: number;
}

const LoanClosingCost:FC<Props> = ({
  value,
  record,
  refetchData,
  calculateColumnTotals,
  setReportLoanClosingCosts,
  loan,
  nonMutableColumnsMapping,
  currentClosingCosts,
  isFirstItemInRefinance,
  loanIndex,
}) => {
  const [updateLoanClosingCostLoading, setUpdateLoanClosingCostLoading] = useState(false);
  const [amountValue, setAmountValue] = useState<string>(value?.amount);
  const [isEditField, setIsEditField] = useState<boolean>(false);

  const isLoanTypeBorrower = loan?.type === 'borrower';

  const {
    isBorrower,
    reportId,
    reportLoanClosingCosts,
    reportClosingCosts,
    report
  } = useContextReports();

  const isRefinance = report?.reportType === EnumReportTypes.refinance;

  const { openNotification } = useContextNotifications();

  const form = Form.useFormInstance();
  const loans: ILoan[] = useWatch('loans', form);

  const rentActive = form.getFieldValue(['tableView', 'rent', 'active']);
  const rent = form.getFieldValue(['loans', loanIndex, 'externalLoan', 'rent'])

  const { fetch: fetchCostAmount } = useUpdateLoansClosingCostAmount();
  const { fetch: reportLoanUpdate } = useReportLoanUpdate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    setAmountValue(event.target?.value.replace(/[^0-9.-]/g, ''));
  };

  const handleColumnClick = (amount: string) => {
    if (isBorrower) return;

    setAmountValue(amount)
    setIsEditField(true);
  };

  const handleBlur = async (loanId: number, closingCostId: number, aprIncluded: boolean) => {
    try {
      setUpdateLoanClosingCostLoading(true)

      const loanIdx = loans.findIndex((item) => item.id === loanId);
      const oldAmount = reportLoanClosingCosts?.find(cost => cost.id === closingCostId)?.amount || 0;

      const numericAmount = parseFloat(amountValue) || 0;

      if (oldAmount === numericAmount) {
        return
      }

      if (aprIncluded) {
        updateAprAndAprRelatedClosingCost(
          form,
          loanIdx,
          oldAmount,
          numericAmount
        )

        const updatedLoans: ILoanValue[] = form?.getFieldValue('loans')

        await reportLoanUpdate({ loans: prepareLoanDataFromForm(updatedLoans) }, reportId)
      }

      setReportLoanClosingCosts(prev => prev.map(cost =>
          cost.id === closingCostId ? { ...cost, amount: numericAmount } : cost
        )
      );

      // Now, update the DB
      await fetchCostAmount({ amount: numericAmount }, closingCostId);
      await refetchData();

      const total = calculateColumnTotals(loanId);
      const newTotal = total - oldAmount + numericAmount;

      const formatedClosingCosts = parseFloat(formatAmount(newTotal))

      if (!isFirstItemInRefinance) {
        form.setFieldValue(['loans', loanIdx, 'closingCosts', 'value'], formatedClosingCosts);
      }

      const loanAmountWithUip = calculateLoanAmountWithUip(form, loanIdx, { closingCosts: formatedClosingCosts  })

      form.setFieldValue(['loans', loanIdx, 'loanAmountWithUip', 'value'], loanAmountWithUip);

      const downPaymentPrice = calculateDownPaymentPrice(form, loanIdx);

      form.setFieldValue(['loans', loanIdx, 'down_payment_price'], downPaymentPrice);

      const updatedLoan = form.getFieldValue(['loans', loanIdx]);

      const principalInterest = calculatePrincipalInterest(updatedLoan);

      form.setFieldValue(['loans', loanIdx, 'principalInterest', 'value'], principalInterest);
    } catch (error) {
      setReportLoanClosingCosts(reportLoanClosingCosts as ILoanClosingCost[]);
      openNotification?.({
        message: 'Error',
        description: 'An error occurred while updating the closing cost amount',
        type: 'error',
      });
    } finally {
      setIsEditField(false)
      setUpdateLoanClosingCostLoading(false);
    }
  };

  // Allow decimals only for percentage fields
  // const handleIsAllowed = (values: NumberFormatValues) =>
  //   !values?.value.includes('.');

  const shouldShowDash =
    isLoanTypeBorrower ||
    (rentActive && rent) ||
    isFirstItemInRefinance ||
    loan?.externalLoan?.closingCostsClearedAt

  if (shouldShowDash) {
    return <p style={{ margin: 0 }}>-</p>;
  }

  if (
    (record.text === nonMutableColumnsMapping.uip && loan.addUip) ||
    (record.text === nonMutableColumnsMapping.vaFundingFee && loan?.externalLoan?.addVaFundingFee)
  ) {
    return <p style={{ margin: 0 }}>Included in loan amount.</p>;
  }

  const loanClosingCost = currentClosingCosts?.find(cost => cost.id === record.id);
  const reportClosingCost = reportClosingCosts?.find(cost => cost.name === loanClosingCost?.name
    // @ts-ignore report id exists here
    && cost.reportId === reportId);

  const isRowActive = reportClosingCost?.active;
  const aprIncluded = reportClosingCost?.aprIncluded;

  const isRowDisabled = !(isRowActive !== false && reportClosingCost);

  if (updateLoanClosingCostLoading) {
    return <Spin size="small" />;
  }

  if (isEditField && value?.editable && value.type !== EnumClosingCostType.prepaidInterest15Days
    && !isRowDisabled && value.type !== EnumClosingCostType.percentageOfLoanAmount
    && value.type !== EnumClosingCostType.percentageOfPurchasePrice
    && value.type !== EnumClosingCostType.propertyTaxes && value.type !== EnumClosingCostType.homeownersInsurance) {
    return (
      <NumericFormat
        value={amountValue}
        thousandSeparator
        onChange={handleChange}
        onBlur={() => handleBlur(loan.id, value?.closingCostId, Boolean(aprIncluded))}
        autoFocus
        prefix="$"
        // isAllowed={handleIsAllowed}
        className="LoanClosingCost-input"
      />
    )
  }

  const isFlatFeeEditable = value?.editable && !isRowDisabled && value.type === EnumClosingCostType.flatFee;

  return (
    <div
      onClick={() => isFlatFeeEditable ? handleColumnClick(value?.amount) : null}
      style={{ cursor: isFlatFeeEditable ? 'pointer' : 'default' }}
    >
      {value?.amount && !isFirstItemInRefinance ? formatAmount(value.amount, { showCurrency: true, max: 2 }) : '$0'}
    </div>
  );
}

export default LoanClosingCost;
